import type { Trade } from "@trader/backtester"
import { BigDecimal } from "effect"
import type { UTCTimestamp } from "lightweight-charts"
import { boxLabels } from "../chart/boxLabels.ts"
import type { TradeBox } from "../chart/tradeBoxPrimitive.ts"

// A strategy's trades as TradingView's long / short position tool (docs/design/0016): a box per
// trade from its entry bar to its exit bar, green from the entry price to its take-profit and red
// to its stop-loss (the levels its strategy.exit set), and a dashed line from the entry to the exit
// fill. A trade with neither level gets one box from the entry to the exit price, green if it won.

/** Report times are epoch milliseconds; the chart's are seconds. */
const seconds = (time: number): UTCTimestamp => Math.round(time / 1000) as UTCTimestamp

/** Each box carries TradingView's labels, in the report's currency (shown when it's wide enough). */
export const tradeBoxes = (trades: ReadonlyArray<Trade>, currency: string): Array<TradeBox> =>
  trades.map((trade) => {
    const from = seconds(trade.entry.time)
    const to = seconds(trade.exit.time)
    const entry = trade.entry.price
    const exit = trade.exit.price
    // Older reports (and runs before the levels existed) have no such fields.
    const stop = trade.stopLoss ?? null
    const target = trade.takeProfit ?? null
    const labels = boxLabels({
      entry,
      stopLoss: stop,
      takeProfit: target,
      qty: BigDecimal.fromNumberUnsafe(trade.qty),
      pnl: BigDecimal.fromNumberUnsafe(trade.profit),
      pnlName: trade.open ? "Open P&L" : "Closed P&L"
    }, null, currency)
    if (stop === null && target === null) {
      const won = trade.profit > 0
      return { from, to, entry, exit, reward: won ? exit : null, risk: won ? null : exit, labels }
    }
    return { from, to, entry, exit, reward: target, risk: stop, labels }
  })

/** "1 : 2.5": the target's distance over the stop's, both from the entry; null without both. */
export const riskReward = (trade: Trade): string | null => {
  const stop = trade.stopLoss ?? null
  const target = trade.takeProfit ?? null
  if (stop === null || target === null) return null
  const risk = Math.abs(trade.entry.price - stop)
  if (risk === 0) return null
  return `1 : ${Number((Math.abs(target - trade.entry.price) / risk).toFixed(2))}`
}
